import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { rpc, useRpcEvent } from '../api/rpc'
import type { ChatSummary, ExtractorOutcome, ExtractorVersion, Routine, RoutineStats } from '../api/types'
import { formatDateTime, formatUsd } from '../lib/format'
import { Button } from './ui/Button'
import { Card } from './ui/Card'
import { Textarea } from './ui/Field'
import { Select } from './ui/Select'
import { useToast } from './ui/Toast'

const TEMPLATE = `export default function (event, ctx) {
  // Return { calls: [{ tool, input }], reply?, result? } to act without the LLM,
  // or null to let the agent handle this event.
  const text = (event && event.text || '').trim()
  if (!text) return null
  return null
}
`

function preview(event: unknown): string {
  if (event && typeof event === 'object' && 'text' in event) return String((event as { text: unknown }).text).slice(0, 120)
  return JSON.stringify(event ?? null).slice(0, 120)
}

/** Extractor (compiled/hybrid mode) editor, tests, versions and fallback stats. */
export function ExtractorPanel({ routine }: { routine: Routine }) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const [versions, setVersions] = useState<ExtractorVersion[]>([])
  const [source, setSource] = useState('')
  const [stats, setStats] = useState<RoutineStats | null>(null)
  const [tests, setTests] = useState<{ event: unknown; outcome: ExtractorOutcome }[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const fail = (err: unknown) => toast((err as Error).message, 'error')

  const load = useCallback(async () => {
    const [v, st] = await Promise.all([
      rpc.call<{ items: ExtractorVersion[] }>('routines.versions', { routineId: routine.id }),
      rpc.call<RoutineStats>('routines.stats', { routineId: routine.id })
    ])
    setVersions(v.items)
    setStats(st)
    setSource(prev => prev || (v.items.find(x => x.active)?.source ?? TEMPLATE))
  }, [routine.id])
  useEffect(() => { void load().catch(() => undefined) }, [load])
  useRpcEvent('routine.run', (r: { routineId: string }) => { if (r.routineId === routine.id) void rpc.call<RoutineStats>('routines.stats', { routineId: routine.id }).then(setStats) })

  const active = versions.find(v => v.active)
  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label)
    try { await fn() } catch (err) { fail(err) } finally { setBusy(null) }
  }

  return (
    <Card title={t('automations.extractor.title')}>
      <p className="mb-4 text-sm text-ink-400">{t('automations.extractor.intro')}</p>
      {stats && stats.runs > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {([
            [t('automations.extractor.compiledRuns'), String(stats.compiled)],
            [t('automations.extractor.agentRuns'), String(stats.agent)],
            [t('automations.extractor.fallbackRate'), `${Math.round(stats.fallbackRate * 100)}%`],
            [t('automations.extractor.savings'), formatUsd(stats.estimatedSavingsUsd)]
          ] as const).map(([label, value]) => (
            <div key={label} className="rounded-xl border border-line px-3 py-2">
              <p className="text-xs text-ink-500">{label}</p>
              <p className="font-display text-lg font-semibold">{value}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {versions.length > 0 && (
          <div className="w-56 max-w-full">
          <Select
            size="sm"
            label={t('automations.extractor.version')}
            value={active ? String(active.version) : ''}
            onChange={version => void run('activate', async () => {
              await rpc.call('routines.activateVersion', { routineId: routine.id, version: Number(version) })
              const v = versions.find(x => x.version === Number(version))
              if (v) setSource(v.source)
              await load()
            })}
            options={versions.map(x => ({
              value: String(x.version),
              label: `v${x.version} · ${t(`automations.extractor.author_${x.author}`)} · ${formatDateTime(x.createdAt, i18n.language)}${x.note ? ` · ${x.note}` : ''}`
            }))}
          />
          </div>
        )}
        <Button size="sm" variant="secondary" loading={busy === 'ai'} onClick={() => void run('ai', async () => {
          const chat = await rpc.call<ChatSummary>('routines.improveExtractor', { routineId: routine.id })
          navigate(`/chat/${chat.id}`)
        })}>{active ? t('automations.extractor.improve') : t('automations.extractor.generate')}</Button>
      </div>
      <Textarea
        aria-label={t('automations.extractor.code')}
        rows={14}
        spellCheck={false}
        className="font-mono text-xs"
        value={source}
        onChange={e => setSource(e.target.value)}
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" loading={busy === 'test'} onClick={() => void run('test', async () => {
          setTests((await rpc.call<{ items: { event: unknown; outcome: ExtractorOutcome }[] }>('routines.testExtractor', { routineId: routine.id, source })).items)
        })}>{t('automations.extractor.test')}</Button>
        <Button size="sm" loading={busy === 'save'} onClick={() => void run('save', async () => {
          await rpc.call('routines.setExtractor', { routineId: routine.id, source })
          toast(t('automations.extractor.saved'), 'success')
          await load()
        })}>{t('automations.extractor.save')}</Button>
      </div>
      {tests && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{t('automations.extractor.results')}</p>
          {tests.length === 0 ? <p className="text-sm text-ink-500">{t('automations.extractor.noEvents')}</p> : (
            <ul className="space-y-2 text-xs">
              {tests.map((x, i) => (
                <li key={i} className="rounded-lg border border-line p-2">
                  <p className="truncate text-ink-300">{preview(x.event)}</p>
                  <pre className={x.outcome.ok ? (x.outcome.action ? 'mt-1 overflow-x-auto font-mono text-mint-500' : 'mt-1 font-mono text-ink-500') : 'mt-1 font-mono text-red-600'}>
                    {x.outcome.ok ? (x.outcome.action ? JSON.stringify(x.outcome.action, null, 2) : t('automations.extractor.toAgent')) : x.outcome.error}
                  </pre>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {stats && stats.recentFallbacks.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{t('automations.extractor.fallbacks')}</p>
          <ul className="space-y-1 text-xs">
            {stats.recentFallbacks.map(f => (
              <li key={f.runId} className="flex gap-2">
                <span className="text-ink-500">{formatDateTime(f.queuedAt, i18n.language)}</span>
                <span className="min-w-0 flex-1 truncate">{preview(f.event)}</span>
                <span className="text-ginger-600">{f.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}
